import { PlugZap, TriangleAlert } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Button, Field, Input, Spinner } from '@/components/ui';
import { probeConnection } from '@/lib/api';
import { config } from '@/lib/config';
import { isUsableApiUrl, normalizeApiUrl, readRecentUrls, type Connection, type ProbeOutcome } from '@/lib/connection';

/**
 * Copy for a probe that did not succeed.
 *
 * `unreachable` names several causes because the browser gives no way to tell them apart:
 * mixed content, a CORS rejection, a denied local-network permission and a refused
 * connection all arrive as the same `TypeError`. Listing them beats a bare "could not
 * connect", which sends people to restart a service that was running the whole time.
 */
function failureMessage(outcome: ProbeOutcome, apiUrl: string): string {
  switch (outcome) {
    case 'needs-token':
      return 'That service is running, and it needs a token: the publicToken from its ~/.fleece/secret.json, or 1234 if that file has none.';
    case 'bad-token':
      return 'That service rejected the token. Check it against the publicToken in the service’s ~/.fleece/secret.json.';
    case 'unreachable':
      return `Nothing answered at ${apiUrl}. The service may be stopped (npm start serves it on :3100), or the browser may have blocked the request before it left: server.corsOrigins in the service’s ~/.fleece/config.json has to include ${window.location.origin} (the default, *, does); a plain http:// address only works on the machine running this browser, and never in Safari; and Chrome asks permission before reaching one.`;
    case 'error':
    case 'ok':
      return `Something answered at ${apiUrl} but it did not look like a Fleece service. Check the address and the port.`;
  }
}

export interface ConnectionFormProps {
  readonly initial?: Connection;
  readonly onConnect: (connection: Connection, remember: boolean) => void;
  readonly submitLabel?: string;
  /** How a candidate failed on load, so the screen opens explaining itself. */
  readonly initialOutcome?: ProbeOutcome;
}

/**
 * Collects a service URL and token, verifies them, and hands back a connection.
 *
 * Shared by the first-run screen and the switch-service dialog, because they ask the same
 * question and a second copy of this form is a second place for the failure copy to drift.
 *
 * Seeded once, on mount. Callers give it a fresh mount when it needs reseeding — the modal
 * renders its body only while open — rather than syncing it from an effect, which would
 * overwrite whatever had been half-typed.
 */
export function ConnectionForm({ initial, onConnect, submitLabel, initialOutcome }: ConnectionFormProps) {
  const [apiUrl, setApiUrl] = useState(initial?.apiUrl ?? config.suggestedApiUrl);
  const [token, setToken] = useState(initial?.token ?? '');
  const [remember, setRemember] = useState(true);
  const [failure, setFailure] = useState<string | undefined>(() =>
    initialOutcome === undefined ? undefined : failureMessage(initialOutcome, initial?.apiUrl ?? config.suggestedApiUrl),
  );
  const [busy, setBusy] = useState(false);
  const [recent] = useState(readRecentUrls);

  const valid = isUsableApiUrl(apiUrl);

  // Verification runs from this submit, so Chrome's local-network permission prompt — which
  // appears when a public page reaches loopback — is provoked by a click the visitor just
  // made, rather than arriving unexplained in the middle of a chart loading.
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!valid || busy) {
      return;
    }
    const trimmedToken = token.trim();
    const candidate: Connection = { apiUrl: normalizeApiUrl(apiUrl), token: trimmedToken.length > 0 ? trimmedToken : undefined };
    setBusy(true);
    setFailure(undefined);
    const outcome = await probeConnection(candidate);
    setBusy(false);

    if (outcome === 'ok') {
      onConnect(candidate, remember);
      return;
    }
    setFailure(failureMessage(outcome, candidate.apiUrl));
  };

  return (
    <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-3">
      <Field label="Service address">
        <Input
          value={apiUrl}
          onChange={(event) => setApiUrl(event.target.value)}
          placeholder={config.suggestedApiUrl}
          autoFocus
          autoComplete="url"
          spellCheck={false}
          className="font-mono"
        />
      </Field>

      {recent.length > 0 && (
        <div className="-mt-1 flex flex-wrap items-center gap-1">
          <span className="text-[11px] text-muted">Recent:</span>
          {recent.map((url) => (
            <button
              key={url}
              type="button"
              onClick={() => setApiUrl(url)}
              className="rounded-sm border border-line px-1.5 py-0.5 font-mono text-[11px] text-ink-2 transition-colors hover:border-accent hover:text-accent focus-visible:outline-2 focus-visible:outline-accent"
            >
              {url}
            </button>
          ))}
        </div>
      )}

      <Field label="Token" hint="publicToken from ~/.fleece/secret.json (1234 if unset)">
        <Input
          type="password"
          value={token}
          onChange={(event) => setToken(event.target.value)}
          placeholder="Leave empty if the service runs without one"
          autoComplete="off"
          spellCheck={false}
          className="font-mono"
        />
      </Field>
      <p className="-mt-2 text-[11px] text-muted">
        Kept in this browser, where any script on this page could read it. On a shared machine, leave &ldquo;Stay connected&rdquo; off so it ends with the tab.
      </p>

      <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-2">
        <input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} className="accent-[var(--accent)]" />
        Stay connected on this browser
      </label>

      {failure !== undefined && (
        <div role="alert" className="flex items-start gap-2 rounded-md border border-negative/30 bg-negative/10 p-2.5 text-xs text-negative">
          <TriangleAlert className="mt-px size-3.5 shrink-0" />
          <p>{failure}</p>
        </div>
      )}

      <Button type="submit" variant="primary" disabled={!valid || busy} className="w-full">
        {busy ? <Spinner className="border-accent-ink/40 border-t-accent-ink" /> : <PlugZap className="size-3.5" />}
        {busy ? 'Checking…' : (submitLabel ?? 'Connect')}
      </Button>
    </form>
  );
}
